import React, { useContext } from 'react';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import { forwardRef } from 'react';

import Box from '../Box';
import Icon from '../Icon';
import { StyleProps, StyledProps } from '@front10';

type NativeSelect = JSX.IntrinsicElements['select'];

type Option = {
  name: string;
  value: string | number;
  key: string;
};

interface AddedProps {
  ariaLabel: NativeSelect['aria-label'];
  ariaDescribedBy: NativeSelect['aria-describedby'];
  options: Option[];
  showIconLeft: boolean;
  showIconRight: boolean;
  fullWidth: boolean;
  focusClass?: string;
  selectStyles: StyleProps['style'];
  iconLeft: string;
  iconRight: string;
  iconLeftStyles: StyleProps;
  iconRightStyles: StyleProps;
  iconLeftIcon: StyleProps;
  iconRightIcon: StyleProps;
  forwardedRef?: any;
  disabled: boolean;
}

type Props = StyledProps<AddedProps> &
  Pick<NativeSelect, 'id' | 'value' | 'onChange' | 'onBlur' | 'onFocus'>;

function Select(props: Props) {
  const {
    ariaLabel,
    ariaDescribedBy,
    options,
    selectStyles,
    id,
    value,
    iconLeft,
    iconRight,
    showIconLeft,
    showIconRight,
    iconLeftStyles,
    iconRightStyles,
    iconLeftIcon,
    iconRightIcon,
    onChange,
    onBlur,
    onFocus,
    fullWidth,
    focusClass,
    forwardedRef,
    disabled
  } = props;
  const { direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const [focus, setFocus] = React.useState(false);
  const styles = { ...style };
  styles.width = fullWidth ? '100%' : 'auto';

  return (
    <Box
      className={`${className} fr-custom-select-container ${focus && focusClass ? focusClass : ''}`}
      style={styles}
    >
      <select
        style={selectStyles}
        ref={forwardedRef}
        id={id}
        value={value}
        className="fr-form-control fr-custom-select"
        onChange={onChange}
        onBlur={(event) => {
          setFocus(false);
          if (onBlur) {
            onBlur(event);
          }
        }}
        onFocus={(event) => {
          setFocus(true);
          if (onFocus) {
            onFocus(event);
          }
        }}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        data-testid="simpleSortingSelect"
        disabled={disabled}
      >
        {options.map((option: Option) => (
          <option key={option.key} value={option.value}>
            {option.name}
          </option>
        ))}
      </select>
      {showIconLeft && (
        <Box {...iconLeftStyles} position="absolute" className="fr-select-left-icon">
          <Icon {...iconLeftIcon} icon={iconLeft} />
        </Box>
      )}
      {showIconRight && (
        <Box {...iconRightStyles} position="absolute" className="fr-select-right-icon">
          <Icon {...iconRightIcon} icon={iconRight} />
        </Box>
      )}
    </Box>
  );
}

Select.defaultProps = {
  options: [],
  id: '',
  value: '',
  iconLeft: 'switch',
  iconRight: 'keyboard_arrow_down',
  showIconLeft: true,
  showIconRight: true,
  fullWidth: false,
  selectStyles: {},
  iconLeftStyles: {
    left: '3',
    top: '2'
  },
  iconLeftIcon: {},
  iconRightStyles: {
    right: '3',
    top: '2'
  },
  iconRightIcon: {},
  border: true
};

Select.componentName = 'Select';

export default forwardRef((props: Props, ref) => <Select {...props} forwardedRef={ref} />);
